/**
 * reminderConfigStore.js — store en memoria (sin backend) de CUÁNDO sale el recordatorio de
 * práctica en el simulador de autoescuela, y el teléfono que se le da al alumno que cancela.
 *
 * Espejo de `settings.reminder_config` de la app real (lo edita RecordatorioHorasEditor dentro de
 * Mensajes automáticos → Recordatorio de práctica). Lo leen: el editor de horas, el modal «Enviar
 * semana» (cuántos recordatorios se programan) y el bloque «Aviso al alumno» de cada práctica
 * (cuándo se le pedirá que la confirme).
 */

// Un solo recordatorio (29/10/2026): el simulador enseña lo que tiene una autoescuela dada de alta desde el 29/10/2026,
// que solo puede tener UNO (las de antes, hasta dos). Ficha: docs/features/autoescuela-un-recordatorio-empresas-nuevas.md
let _state = {
  hours: [24],
  cancel_contact_phone: "",
};
const _subs = new Set();

export function getReminderConfig() {
  return _state;
}

export function setReminderConfig(next) {
  _state = { ..._state, ...next };
  _subs.forEach((fn) => {
    try { fn(_state); } catch { /* un suscriptor roto no tumba a los demás */ }
  });
}

export function subscribeReminderConfig(fn) {
  _subs.add(fn);
  return () => _subs.delete(fn);
}

// ── Helpers puros (copias de apps/driving-school/src/components/mensajes/RecordatorioHorasEditor.jsx) ──
// 1 siempre en el simulador (David, 29/10/2026): es lo que tiene una autoescuela nueva (`recordatorios_max` = 1 en la app).
export const MAX_REMINDERS = 1;
const E164 = /^\+[1-9]\d{7,14}$/;

/** «+34600111222» → { prefix: "+34", number: "600111222" }. */
export function splitE164(phone, countries = []) {
  if (!phone || typeof phone !== "string" || !phone.startsWith("+")) return { prefix: "+34", number: "" };
  const match = [...countries]
    .filter((c) => phone.startsWith(c.dial_code))
    .sort((a, b) => b.dial_code.length - a.dial_code.length)[0];
  if (!match) return { prefix: "+34", number: phone.slice(1) };
  return { prefix: match.dial_code, number: phone.slice(match.dial_code.length) };
}

/** Teléfono a guardar: "" si no hay número; null si está mal (no se guarda). */
export function joinPhone(prefix, number) {
  const digits = String(number || "").replace(/\D/g, "");
  if (!digits) return "";
  const full = `${prefix || "+34"}${digits}`;
  return E164.test(full) ? full : null;
}

/** Código del error de validación de las horas (o null si están bien). */
export function hoursErrorCode(hours) {
  if (hours.length > MAX_REMINDERS) return "max";
  if (hours.some((h) => !Number.isInteger(h) || h < 1 || h > 168)) return "range";
  if (new Set(hours).size !== hours.length) return "duplicate";
  return null;
}

/**
 * Cómo y cuándo confirma el alumno su práctica (copia de confirmacionEstado del PracticaModal real).
 * En el simulador las prácticas no traen `reminder_schedules`: se calculan con las horas del store
 * (inicio − horas), igual que haría c_putPractica al guardarla.
 */
export function confirmacionEstado(p = {}, hours = [], now = Date.now()) {
  // «No puede» (9/10/2026, Elices): lo escribió por WhatsApp; manda sobre lo demás.
  if (p.no_puede_at && p.status !== "cancelada") return { kind: "no_puede", at: p.no_puede_at, texto: p.no_puede_texto || null };
  if (p.confirmed === true) return { kind: "confirmada", at: p.confirmed_at || null, via: p.confirmed_via || null };
  if (p.reminder_sent === true) return { kind: "recordatorio_enviado" };
  const start = p.start ? new Date(p.start).getTime() : NaN;
  const proximos = Number.isNaN(start)
    ? []
    : hours
      .map((h) => start - Number(h) * 3600 * 1000)
      .filter((t) => t > now)
      .sort((a, b) => a - b);
  if (proximos.length) return { kind: "recordatorio_programado", at: new Date(proximos[0]).toISOString() };
  return { kind: "sin_recordatorio" };
}
